import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, CheckCircle2, AlertTriangle, Info,
  ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";
import api from "@/lib/api";

// Top-of-Dashboard banner: current Visa Bulletin month + each case's standing.
const SEVERITY_STYLES = {
  success:  { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534", icon: CheckCircle2 },
  info:     { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF", icon: Info },
  warning:  { bg: "#FFFBEB", border: "#FED7AA", text: "#B45309", icon: AlertTriangle },
  attention:{ bg: "#FEF2F2", border: "#FECACA", text: "#B91C1C", icon: AlertTriangle },
};

function useAllCasesStatus() {
  const [state, setState] = useState({ loading: true, cases: [], bulletin: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bulletinResp, casesResp] = await Promise.all([
          api.get("/visa-bulletin/latest"),
          api.get("/cases"),
        ]);
        const cases = casesResp.data || [];
        const statuses = await Promise.all(
          cases.map((c) =>
            api.get(`/cases/${c.id}/visa-status`)
              .then((r) => ({ case: c, status: r.data }))
              .catch(() => null)
          )
        );
        if (!cancelled) {
          setState({
            loading: false,
            bulletin: bulletinResp.data?.bulletin || null,
            cases: statuses.filter(Boolean),
          });
        }
      } catch {
        if (!cancelled) setState({ loading: false, cases: [], bulletin: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

function TrackedCaseRow({ c, status }) {
  const sty = SEVERITY_STYLES[status.banner_severity] || SEVERITY_STYLES.info;
  const Icon = sty.icon;
  const ben =
    `${c.data?.beneficiary_first_name || ""} ${c.data?.beneficiary_last_name || ""}`.trim() ||
    "Untitled applicant";
  return (
    <Link
      to={`/case/${c.id}`}
      className="flex items-start gap-3 p-3 rounded-md border"
      style={{ background: sty.bg, borderColor: sty.border }}
      data-testid={`visa-bulletin-row-${c.id}`}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: sty.text }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-display font-bold text-sm" style={{ color: sty.text }}>
            {ben}
          </span>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "rgba(255,255,255,.7)", color: sty.text }}>
            {status.category || "—"} · {status.country || "ALL"}
          </span>
        </div>
        <div className="text-[13px] leading-relaxed" style={{ color: sty.text }}>
          {status.banner_message}
        </div>
        <div className="text-[11px] mt-1" style={{ color: sty.text, opacity: 0.85 }}>
          Priority date: <b>{status.priority_date || "—"}</b> ·
          {" "}Cutoff: <b>{status.fad_cutoff || "—"}</b>
          {status.estimated_wait_months > 0 && (
            <> · Est. wait: <b>~{status.estimated_wait_months} mo</b></>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function VisaBulletinBanner() {
  const { loading, cases, bulletin } = useAllCasesStatus();
  const [expanded, setExpanded] = useState(true);

  if (loading) return null;

  const tracked = cases.filter((cs) => cs.status?.tracked);
  const untrackedCount = cases.length - tracked.length;

  if (tracked.length === 0 && !bulletin) return null;

  const monthLabel = bulletin?.month
    ? new Date(bulletin.month + "-01").toLocaleString("en-US", { month: "long", year: "numeric" })
    : "Awaiting first fetch";

  return (
    <div
      className="mb-6 rounded-lg border overflow-hidden"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="visa-bulletin-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 text-left"
        data-testid="visa-bulletin-toggle"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
          >
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-extrabold text-base" style={{ color: "var(--text-primary)" }}>
                Visa Bulletin — {monthLabel}
              </span>
              {tracked.length > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: "#DCFCE7", color: "#166534" }}
                >
                  {tracked.length} case{tracked.length !== 1 ? "s" : ""} tracked
                </span>
              )}
              {untrackedCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: "#FEF3C7", color: "#78350F" }}
                >
                  {untrackedCount} untracked
                </span>
              )}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
              Updated the 15th of every month · A current priority date means your DS-260 case can proceed
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: "var(--border-stone)" }}>
          {tracked.length === 0 ? (
            <div className="pt-4 text-center">
              <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
                No cases are being tracked yet. Open any case → the "Priority Date"
                card lets you enter your priority date and preference category.
              </p>
              {bulletin?.source_url && (
                <a
                  href={bulletin.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold"
                  style={{ color: "var(--action)" }}
                >
                  See the current Visa Bulletin <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ) : (
            <div className="pt-3 space-y-2">
              {tracked.map(({ case: c, status }) => (
                <TrackedCaseRow key={c.id} c={c} status={status} />
              ))}
              {bulletin?.source_url && (
                <div className="mt-2 text-right">
                  <a
                    href={bulletin.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: "var(--action)" }}
                  >
                    Full Visa Bulletin (travel.state.gov) <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
